/**
 * NotificationItem Component
 *
 * Displays a single notification item in the notification drawer.
 * Shows task context, preview text, timestamp, and author info.
 * Clickable - navigates to the specific task when clicked.
 */

import { MessageSquare, FileText, Share2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/shared/ui/badge";

interface NotificationItemProps {
  id: string;
  type: "comment" | "note" | "worksheet";
  taskTitle: string;
  preview: string;
  timestamp: string;
  authorName?: string;
  clientName?: string;
  isPromptResponse?: boolean;
  actionType?: string;
  onClick: () => void;
  className?: string;
  isRead?: boolean;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  type,
  taskTitle,
  preview,
  timestamp,
  authorName,
  clientName,
  isPromptResponse,
  actionType,
  onClick,
  className,
  isRead = false,
}) => {
  const { t } = useTranslation();

  // Format timestamp
  const formattedTime = new Date(timestamp).toLocaleString("da-DK", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  // Determine icon and label based on type
  const icon =
    type === "comment"
      ? MessageSquare
      : type === "worksheet"
        ? Share2
        : FileText;
  const Icon = icon;

  const label =
    type === "comment"
      ? t("notifications.fromTherapist", "Fra din terapeut")
      : type === "worksheet"
        ? actionType === "feedback_request"
          ? t("notifications.feedbackRequested", "Anmodede om feedback")
          : t("notifications.worksheetShared", "Delte et arbejdsark svar")
        : isPromptResponse
          ? t("notifications.promptResponse", "Svar på refleksionsspørgsmål")
          : t("notifications.sharedNote", "Ny delt notat");

  const displayName = type === "comment" ? authorName : clientName;

  return (
    <button
      onClick={onClick}
      data-testid="notification-item"
      className={cn(
        "w-full text-left p-4 rounded-lg border border-border transition-colors",
        "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
        "min-touch-target",
        isRead
          ? "bg-muted/50 hover:bg-muted opacity-70"
          : "bg-card hover:bg-accent",
        className
      )}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-1">
          <div
            className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center",
              isRead
                ? "bg-gray-100 dark:bg-gray-800"
                : "bg-orange-100 dark:bg-orange-900/20"
            )}
          >
            <Icon
              className={cn(
                "h-4 w-4",
                isRead
                  ? "text-gray-400 dark:text-gray-600"
                  : "text-orange-600 dark:text-orange-400"
              )}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-1">
          {/* Header */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p
                className="text-sm font-medium text-foreground truncate"
                data-testid="task-title"
              >
                {taskTitle}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-xs text-muted-foreground">{label}</p>
                {type === "worksheet" && actionType === "feedback_request" && (
                  <Badge variant="secondary" className="text-xs">
                    {t(
                      "notifications.feedbackRequestedBadge",
                      "Feedback anmodet"
                    )}
                  </Badge>
                )}
              </div>
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
              {formattedTime}
            </span>
          </div>

          {/* Preview */}
          <p className="text-sm text-muted-foreground line-clamp-2">
            {preview}
          </p>

          {/* Author/Client name */}
          {displayName && (
            <p
              className="text-xs text-muted-foreground"
              data-testid="author-name"
            >
              {type === "comment"
                ? t("notifications.from", "Fra")
                : t("notifications.by", "Af")}
              : {displayName}
            </p>
          )}

          {/* Call to action */}
          <p
            className={cn(
              "text-xs font-medium",
              isRead
                ? "text-muted-foreground"
                : "text-orange-600 dark:text-orange-400"
            )}
          >
            {t("notifications.clickToView", "Klik for at se")} →
          </p>
        </div>
      </div>
    </button>
  );
};
